import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function generateId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 9)}`
}

/** Convert a string to a URL-safe slug: lowercase, no special chars, dashes for spaces */
export function slugify(text: string): string {
  return text
    .normalize('NFD')                    // decompose accented characters
    .replace(/[\u0300-\u036f]/g, '')     // strip diacritical marks
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')        // remove non-alphanumeric (except spaces/dashes)
    .replace(/[\s_]+/g, '-')             // spaces/underscores → dashes
    .replace(/-+/g, '-')                 // collapse multiple dashes
    .replace(/^-|-$/g, '')               // trim leading/trailing dashes
}

export function formatDate(date: string | Date): string {
  return new Date(date).toLocaleString()
}

/**
 * Random token for public gallery/kiosk share links. Shared by the settings
 * pages and by event import, which must never reuse the source event's token.
 */
export function generateShareToken(): string {
  return Array.from({ length: 16 }, () => Math.floor(Math.random() * 36).toString(36)).join('')
}

/** Absolute expiry for a share link, or null when access is permanent. */
export function calculateExpiration(
  duration: 'hours' | 'days' | 'permanent',
  value: number
): string | null {
  if (duration === 'permanent') return null
  const now = new Date()
  if (duration === 'hours') {
    now.setHours(now.getHours() + value)
  } else if (duration === 'days') {
    now.setDate(now.getDate() + value)
  }
  return now.toISOString()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce<T extends (...args: any[]) => void>(fn: T, ms: number) {
  let timer: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timer)
    timer = setTimeout(() => fn(...args), ms)
  }
}

/**
 * fetch with a hard timeout.
 *
 * A plain `fetch` never gives up on a *stalled* (as opposed to failed)
 * connection — the browser can wait many minutes. On a weak 4G/5G hotspot at
 * a venue that leaves an unattended booth spinning forever with no recovery.
 * Every network call in the booth path should go through this.
 */
export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs = 20000
): Promise<Response> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetch(input, { ...init, signal: controller.signal })
  } finally {
    clearTimeout(timer)
  }
}

export interface RetryOptions {
  timeoutMs?: number
  retries?: number
  /** Called before each retry, so callers can surface progress to the guest. */
  onRetry?: (attempt: number, totalRetries: number) => void
}

/**
 * fetch with timeout plus exponential-backoff retry.
 *
 * Retries only what a retry can actually fix: network errors, timeouts, and
 * 5xx. A 4xx is a client mistake and will fail identically every time, so it
 * returns immediately rather than burning ~15s of an event guest's patience.
 */
export async function fetchWithRetry(
  input: RequestInfo | URL,
  init: RequestInit = {},
  { timeoutMs = 20000, retries = 3, onRetry }: RetryOptions = {}
): Promise<Response> {
  let lastError: unknown

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetchWithTimeout(input, init, timeoutMs)
      if (res.ok || res.status < 500) return res
      lastError = new Error(`Server responded ${res.status}`)
    } catch (err) {
      lastError = err
    }

    if (attempt < retries) {
      onRetry?.(attempt + 1, retries)
      // 1s, 2s, 4s … capped so a long stall can't hang the booth indefinitely.
      await new Promise((r) => setTimeout(r, Math.min(1000 * 2 ** attempt, 8000)))
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Request failed after retries')
}

/** Read the mime type out of a `data:` URL, e.g. "image/jpeg" */
export function mimeFromDataUrl(dataUrl: string): string {
  return dataUrl.match(/^data:(.*?);/)?.[1] || 'image/jpeg'
}

/** File extension (no dot) matching a data URL's mime type, for use as an upload filename */
export function extensionFromDataUrl(dataUrl: string): string {
  const mime = mimeFromDataUrl(dataUrl)
  if (mime === 'image/png') return 'png'
  if (mime === 'image/webp') return 'webp'
  return 'jpg'
}

/**
 * Re-encode a captured photo/layout dataURL for network upload: downscale to
 * `maxDimension` on the long edge and re-encode at `quality`. Used by Data Saver
 * mode to shrink what gets sent over the wire without touching the original
 * full-quality copy still held in memory (used for printing).
 * PNGs (e.g. green-screen cutouts with no background) keep their format so
 * transparency isn't lost — only their dimensions are reduced.
 */
export function compressDataUrlForUpload(dataUrl: string, quality: number, maxDimension: number): Promise<string> {
  return new Promise((resolve) => {
    const mime = mimeFromDataUrl(dataUrl)
    const img = new Image()
    img.onload = () => {
      let { width, height } = img
      const scale = Math.min(1, maxDimension / Math.max(width, height))
      width = Math.max(1, Math.round(width * scale))
      height = Math.max(1, Math.round(height * scale))

      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve(dataUrl)
        return
      }
      ctx.drawImage(img, 0, 0, width, height)
      resolve(mime === 'image/png' ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', quality))
    }
    img.onerror = () => resolve(dataUrl)
    img.src = dataUrl
  })
}

export function dataURLToBlob(dataUrl: string): Blob {
  const arr = dataUrl.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png'
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new Blob([u8arr], { type: mime })
}

export async function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

/**
 * Re-encode a photo/layout dataURL as lossless PNG. The shared "master" copy
 * used for print and the baseline (non-Data-Saver) upload is JPEG at quality
 * 0.92 — very high quality, but still lossy compression. The local HQ archive
 * should genuinely be uncompressed, so this guarantees no further generation
 * loss happens between what's on screen and what lands on disk.
 */
export function toLosslessPng(dataUrl: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve(dataUrl)
        return
      }
      ctx.drawImage(img, 0, 0)
      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = () => resolve(dataUrl)
    img.src = dataUrl
  })
}

/** True only inside the Electron kiosk client (not a plain browser tab). */
export function isElectronClient(): boolean {
  return !!window.electronAPI?.isElectron?.()
}

/**
 * Best-effort local archive: when running inside the Electron kiosk client,
 * writes the untouched full-quality photo/video straight to disk on this
 * machine, independent of whatever gets uploaded to the server (e.g. a
 * Data-Saver-shrunk copy sent over a weak connection). No-op in a plain
 * browser tab. Never throws — a failed local save should never break the
 * booth's normal save/upload flow.
 */
export async function saveHQPhotoLocally(eventName: string, sessionId: string, filename: string, dataUrlOrBlob: string | Blob): Promise<void> {
  if (!window.electronAPI?.isElectron?.()) return
  try {
    const blob = typeof dataUrlOrBlob === 'string' ? dataURLToBlob(dataUrlOrBlob) : dataUrlOrBlob
    const data = await blob.arrayBuffer()
    const result = await window.electronAPI.saveHQPhoto({ eventName, sessionId, filename, data })
    if (!result.success) {
      console.error('Local HQ save failed:', result.error)
    }
  } catch (err) {
    console.error('Local HQ save failed:', err)
  }
}

export async function uploadFile(file: File): Promise<string | null> {
  const formData = new FormData()
  formData.append('file', file)
  try {
    const res = await fetch('/api/upload', {
      method: 'POST',
      body: formData,
    })
    if (!res.ok) throw new Error('Upload failed')
    const data = await res.json()
    return data.url
  } catch (err) {
    console.error('Upload error:', err)
    return null
  }
}
